import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import QRCode from "qrcode";
import { API } from "@/App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import {
  QrCode, Share2, Copy, Download, Image as ImageIcon, RefreshCcw
} from "lucide-react";
import { getTenantSlugFromPath } from "@/lib/tenant";

const QR_SIZE_PX = 1024;
const LOGO_RATIO = 0.22; // 22% of QR width

// Convert a logo image URL to a data URL so qrcode lib (and html-to-canvas) can
// draw it cleanly without CORS surprises.
async function urlToDataUrl(url) {
  if (!url) return "";
  if (url.startsWith("data:")) return url;
  const absolute = url.startsWith("http") ? url : `${window.location.origin}${url}`;
  try {
    const res = await fetch(absolute, { credentials: "omit" });
    if (!res.ok) throw new Error("fetch logo");
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(blob);
    });
  } catch {
    return "";
  }
}

function dataUrlImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

async function buildQrCanvas({ value, color, bgColor, logoDataUrl, withLogo }) {
  const canvas = document.createElement("canvas");
  canvas.width = QR_SIZE_PX;
  canvas.height = QR_SIZE_PX;
  await QRCode.toCanvas(canvas, value || " ", {
    width: QR_SIZE_PX,
    margin: 2,
    errorCorrectionLevel: withLogo ? "H" : "M",
    color: { dark: color, light: bgColor },
  });
  if (withLogo && logoDataUrl) {
    try {
      const img = await dataUrlImage(logoDataUrl);
      const ctx = canvas.getContext("2d");
      const logoW = Math.round(QR_SIZE_PX * LOGO_RATIO);
      const x = (QR_SIZE_PX - logoW) / 2;
      const y = (QR_SIZE_PX - logoW) / 2;
      // White rounded backdrop for legibility
      const pad = Math.round(logoW * 0.12);
      const bx = x - pad;
      const by = y - pad;
      const bw = logoW + pad * 2;
      const radius = Math.round(bw * 0.12);
      ctx.fillStyle = bgColor;
      ctx.beginPath();
      ctx.moveTo(bx + radius, by);
      ctx.lineTo(bx + bw - radius, by);
      ctx.quadraticCurveTo(bx + bw, by, bx + bw, by + radius);
      ctx.lineTo(bx + bw, by + bw - radius);
      ctx.quadraticCurveTo(bx + bw, by + bw, bx + bw - radius, by + bw);
      ctx.lineTo(bx + radius, by + bw);
      ctx.quadraticCurveTo(bx, by + bw, bx, by + bw - radius);
      ctx.lineTo(bx, by + radius);
      ctx.quadraticCurveTo(bx, by, bx + radius, by);
      ctx.closePath();
      ctx.fill();
      ctx.drawImage(img, x, y, logoW, logoW);
    } catch {
      // Silently fall back to QR without the logo
    }
  }
  return canvas;
}

async function buildQrSvg({ value, color, bgColor }) {
  return QRCode.toString(value || " ", {
    type: "svg",
    margin: 2,
    errorCorrectionLevel: "M",
    color: { dark: color, light: bgColor },
  });
}

function download(dataUrl, filename) {
  const a = document.createElement("a");
  a.href = dataUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export default function SharingTools() {
  const tenantSlug = getTenantSlugFromPath();
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const tenantBase = `${origin}/${tenantSlug}`;

  const [settings, setSettings] = useState({});
  const [competitions, setCompetitions] = useState([]);
  const [logoDataUrl, setLogoDataUrl] = useState("");

  // QR form state
  const [preset, setPreset] = useState("homepage");
  const [url, setUrl] = useState(tenantBase);
  const [withLogo, setWithLogo] = useState(true);
  const [color, setColor] = useState("#111111");
  const [bgColor, setBgColor] = useState("#ffffff");
  const [previewSrc, setPreviewSrc] = useState("");
  const canvasRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/settings`)
      .then((r) => setSettings(r.data || {}))
      .catch(() => {});
    axios.get(`${API}/competitions`)
      .then((r) => setCompetitions(r.data || []))
      .catch(() => {});
  }, []);

  // Pre-resolve logo to a data URL (avoids CORS on canvas export)
  useEffect(() => {
    const candidate = settings.icon_logo_url || settings.logo_url || "";
    if (!candidate) { setLogoDataUrl(""); return; }
    urlToDataUrl(candidate).then(setLogoDataUrl);
  }, [settings.icon_logo_url, settings.logo_url]);

  const presets = useMemo(() => {
    const items = [
      { key: "homepage",     label: "Homepage",          url: tenantBase },
      { key: "book",         label: "Booking Wizard",    url: `${tenantBase}/book` },
      { key: "vouchers",     label: "Buy Gift Voucher",  url: `${tenantBase}/gift-vouchers` },
      { key: "reviews",      label: "Reviews Page",      url: `${tenantBase}/reviews` },
      { key: "consultation", label: "Consultation Form", url: `${tenantBase}/consultation` },
    ];
    competitions.forEach((c) => {
      items.push({
        key: `competition-${c.id}`,
        label: `Competition · ${c.name}`,
        url: `${tenantBase}/competition/${c.slug}`,
      });
    });
    items.push({ key: "custom", label: "Custom URL…", url: "" });
    return items;
  }, [tenantBase, competitions]);

  const onPresetChange = (key) => {
    setPreset(key);
    const p = presets.find((x) => x.key === key);
    if (p && p.key !== "custom") setUrl(p.url);
    else if (p && p.key === "custom") setUrl("");
  };

  // Regenerate preview whenever any input changes
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const canvas = await buildQrCanvas({
          value: url || tenantBase,
          color,
          bgColor,
          logoDataUrl,
          withLogo,
        });
        if (cancelled) return;
        canvasRef.current = canvas;
        setPreviewSrc(canvas.toDataURL("image/png"));
      } catch (e) {
        if (!cancelled) toast.error("Could not render QR code");
      }
    })();
    return () => { cancelled = true; };
  }, [url, color, bgColor, withLogo, logoDataUrl, tenantBase]);

  const filenameBase = useMemo(() => {
    const slug = (url || "qr").replace(/^https?:\/\//, "").replace(/[^a-z0-9-]+/gi, "-").replace(/^-+|-+$/g, "").toLowerCase();
    return `${tenantSlug || "tenant"}-qr-${slug || "code"}`.slice(0, 80);
  }, [url, tenantSlug]);

  const downloadPng = async () => {
    try {
      const canvas = await buildQrCanvas({ value: url || tenantBase, color, bgColor, logoDataUrl, withLogo });
      download(canvas.toDataURL("image/png"), `${filenameBase}.png`);
      toast.success("PNG downloaded");
    } catch {
      toast.error("Download failed");
    }
  };

  const downloadSvg = async () => {
    try {
      // SVG can't easily inline external logo cross-origin; we offer plain SVG
      // and let users embed the logo manually in their design tool.
      const svg = await buildQrSvg({ value: url || tenantBase, color, bgColor });
      const blob = new Blob([svg], { type: "image/svg+xml" });
      const dataUrl = URL.createObjectURL(blob);
      download(dataUrl, `${filenameBase}.svg`);
      setTimeout(() => URL.revokeObjectURL(dataUrl), 1000);
      toast.success(withLogo ? "SVG downloaded (logo not embedded in SVG)" : "SVG downloaded");
    } catch {
      toast.error("Download failed");
    }
  };

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch {
      toast.error("Couldn't copy");
    }
  };

  // ── Shareable link with rich preview (moved from Settings) ──
  const richShareUrl = tenantSlug ? `${origin}/api/share/${tenantSlug}` : "";

  return (
    <div className="space-y-6" data-testid="sharing-tools-page">
      <Toaster position="top-center" />
      <div>
        <h1 className="text-2xl font-semibold flex items-center gap-2" style={{ fontFamily: "Playfair Display" }}>
          <QrCode size={22} className="text-primary" />
          Sharing Tools
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          QR codes for any tenant URL, plus a rich-preview link for social posts.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ── QR Generator ── */}
        <Card className="p-5 space-y-4" data-testid="qr-generator-card">
          <div>
            <h2 className="text-base font-semibold flex items-center gap-2">
              <QrCode size={16} className="text-primary" /> QR Code Generator
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Pick a preset or paste any URL. Download in PNG (with optional logo) or SVG.
            </p>
          </div>

          <div className="space-y-3">
            <div>
              <Label className="text-xs">Preset</Label>
              <Select value={preset} onValueChange={onPresetChange}>
                <SelectTrigger className="mt-1 rounded-xl" data-testid="qr-preset-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {presets.map((p) => (
                    <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs">URL</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={url}
                  onChange={(e) => { setUrl(e.target.value); setPreset("custom"); }}
                  placeholder="https://..."
                  className="rounded-xl font-mono text-xs"
                  data-testid="qr-url-input"
                />
                <Button variant="outline" size="icon" className="rounded-xl shrink-0" onClick={copyUrl} title="Copy link" data-testid="qr-copy-url">
                  <Copy size={14} />
                </Button>
              </div>
            </div>

            <div className="flex items-center justify-between bg-muted/40 p-3 rounded-xl">
              <div className="flex items-center gap-2">
                <ImageIcon size={14} className="text-primary" />
                <div>
                  <Label className="cursor-pointer text-xs">Logo in center</Label>
                  <p className="text-[10px] text-muted-foreground">Uses your salon icon/logo for branded scans.</p>
                </div>
              </div>
              <Switch checked={withLogo} onCheckedChange={setWithLogo} disabled={!logoDataUrl} data-testid="qr-logo-toggle" />
            </div>
            {withLogo && !logoDataUrl && (
              <p className="text-[10px] text-amber-700">
                Upload a logo in Settings → Branding to enable the logo overlay.
              </p>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">QR Colour</Label>
                <div className="flex gap-2 mt-1">
                  <Input type="color" value={color} onChange={(e) => setColor(e.target.value)} className="w-12 rounded-xl p-1 h-9" data-testid="qr-color-input" />
                  <Input value={color} onChange={(e) => setColor(e.target.value)} className="rounded-xl text-xs font-mono" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Background</Label>
                <div className="flex gap-2 mt-1">
                  <Input type="color" value={bgColor} onChange={(e) => setBgColor(e.target.value)} className="w-12 rounded-xl p-1 h-9" data-testid="qr-bg-input" />
                  <Input value={bgColor} onChange={(e) => setBgColor(e.target.value)} className="rounded-xl text-xs font-mono" />
                </div>
              </div>
            </div>

            <Button variant="ghost" size="sm" className="rounded-xl" onClick={() => { setColor("#111111"); setBgColor("#ffffff"); setWithLogo(!!logoDataUrl); }} data-testid="qr-reset-btn">
              <RefreshCcw size={12} className="mr-1.5" /> Reset to defaults
            </Button>
          </div>

          {/* Preview */}
          <div className="border border-dashed rounded-2xl p-6 flex items-center justify-center bg-muted/20">
            {previewSrc ? (
              <img src={previewSrc} alt="QR preview" className="w-56 h-56 rounded-xl shadow-sm" data-testid="qr-preview-img" />
            ) : (
              <div className="w-56 h-56 flex items-center justify-center text-xs text-muted-foreground">Generating…</div>
            )}
          </div>

          <div className="flex gap-2">
            <Button onClick={downloadPng} className="rounded-xl flex-1" data-testid="qr-download-png">
              <Download size={14} className="mr-1.5" /> PNG
            </Button>
            <Button onClick={downloadSvg} variant="outline" className="rounded-xl flex-1" data-testid="qr-download-svg">
              <Download size={14} className="mr-1.5" /> SVG
            </Button>
          </div>
        </Card>

        {/* ── Rich Preview Share Link (moved from Settings) ── */}
        <Card className="p-5 space-y-4" data-testid="rich-share-card">
          <div>
            <h2 className="text-base font-semibold flex items-center gap-2">
              <Share2 size={16} className="text-primary" /> Shareable link with rich preview
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              When you paste your salon's regular URL into WhatsApp / Facebook / Twitter, the link preview
              shows our platform's default card. Use the link below instead — it returns your salon's own
              title, description and image to social link scrapers, and bounces real visitors to your normal page.
            </p>
          </div>

          {richShareUrl ? (
            <div className="space-y-3">
              <div className="flex gap-2">
                <Input
                  readOnly
                  value={richShareUrl}
                  className="rounded-xl font-mono text-xs"
                  onFocus={(e) => e.target.select()}
                  data-testid="rich-share-input"
                />
                <Button
                  variant="outline"
                  className="rounded-xl shrink-0"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(richShareUrl);
                      toast.success("Share link copied");
                    } catch {
                      toast.error("Could not copy — please copy manually");
                    }
                  }}
                  data-testid="rich-share-copy"
                >
                  <Copy size={14} className="mr-1.5" /> Copy
                </Button>
              </div>
              <ul className="text-xs text-muted-foreground space-y-1 pl-4 list-disc">
                <li>Share this URL on WhatsApp / Facebook / Instagram — the preview shows <strong>{settings.salon_name || "your salon"}'s</strong> title, description and image.</li>
                <li>When a real person taps the preview, they're auto-redirected to your normal salon page.</li>
                <li>
                  Note: WhatsApp caches link previews for ~48 hrs — first share may take a moment, or use Facebook's
                  {" "}<a href="https://developers.facebook.com/tools/debug/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Sharing Debugger</a>{" "}
                  to force a refresh.
                </li>
              </ul>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">No tenant detected — open this page from a tenant URL.</p>
          )}

          <div className="border-t pt-4">
            <p className="text-[11px] text-muted-foreground">
              Tip: pair this rich preview link with a printed QR code on your salon counter,
              business cards, packaging, or window decal — visitors scan and land on your site
              with a polished preview if shared onward.
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
}
